import { Alert, Box, FormGroup, Spinner } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { PractitionerName } from '@common/interfaces/practitionerName';
import type {
    CareProviderOrganizationsPractitioners,
    InitialOrganizationPractitioner,
} from '@frontend/components/elements/CareProviderSelection';
import type { IOrganization } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { buildLoadOrganizationsPractitionersUrl } from '@frontend/components/elements/careProviderSelector/utils/buildLoadOrganizationsPractitionersUrl';
import { loadOrganization } from '@frontend/components/elements/careProviderSelector/utils/loadOrganization';
import { loadPractitioner } from '@frontend/components/elements/careProviderSelector/utils/loadPractitioner';
import { GeneralPractitionerSearch } from '@frontend/components/elements/GeneralPractitionerSearch';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';

type GeneralPractitionerSearchProps = {
    error?: string;
    formlabeltext: string;
    hasErrors: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    isInModal?: boolean;
    itemsPerPage: number;
    onChange: (organizationId: string | null, practitionerId: string | null) => void;
    onDoValidate?: () => void;
    required: boolean;
    size: 'medium' | 'large';
};

type Practitioner = PractitionerName & {
    id: string;
};

export function GeneralPractitionerSearchWrapper(props: GeneralPractitionerSearchProps) {
    const loggedInUserHasGeneralPractitionersInOrganization = useSelector(
        (state: IReduxState) => state.authentication.organizationHasGeneralPractitionerOrSupervisor
    );

    const translate = getSelectedLanguage();

    const [initialOrganizationPractitioner, setInitialOrganizationPractitioner] = useState<
        InitialOrganizationPractitioner | undefined
    >(undefined);

    const [organizationPractitioners, setOrganizationPractitioners] = useState<
        CareProviderOrganizationsPractitioners[] | undefined
    >(undefined);
    const [total, setTotal] = useState(0);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            await loadInitialOrganizationPractitioner();
            setIsLoading(false);
        };

        loadInitialData();
    }, []);

    if (isLoading) {
        return (
            <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                minHeight="50px"
            >
                <Spinner />
            </Box>
        );
    }

    return (
        <FormGroup
            hasErrors={props.hasErrors}
            isRequired={props.required}
            label={props.formlabeltext || ''}
            layout={props.isInModal ? 'vertical' : 'horizontal'}
        >
            <GeneralPractitionerSearch
                clearSelectedItem={async () =>
                    await props.onChange(
                        initialOrganizationPractitioner?.id || null,
                        initialOrganizationPractitioner?.practitioner.id || null
                    )
                }
                error={props.error}
                initialOrganizationId={props.initialOrganizationId}
                initialOrganizationPractitioner={initialOrganizationPractitioner}
                initialPractitionerId={props.initialPractitionerId}
                itemsPerPage={props.itemsPerPage}
                onChange={async (selectedOption) => {
                    await onChange(selectedOption);
                    await props.onDoValidate?.();
                }}
                onSearch={loadOrganizationsPractitioners}
                organizationPractitioners={organizationPractitioners}
                total={total}
            />
            {organizationPractitioners?.length === 0 && (
                <Alert appearance="info">{translate.practitioner.noSearchResultGeneralPractitioner}</Alert>
            )}
        </FormGroup>
    );

    async function loadOrganizationsPractitioners(searchInput?: string, city?: string, page?: string) {
        if (!searchInput && !city) {
            setOrganizationPractitioners(undefined);

            return;
        }
        try {
            const url = buildLoadOrganizationsPractitionersUrl(searchInput, city, page, props.itemsPerPage);
            const result = await fetchJson(url, 'GET');

            setTotal(result.json.total);
            setOrganizationPractitioners(result.json.items);

            return;
        } catch (error) {
            console.error('Failed to load organizations and practitioners:', error);
        }
    }

    async function onChange(organizationPractitionerIds?: string) {
        if (!organizationPractitionerIds || !organizationPractitionerIds.includes('_')) {
            return await props.onChange(null, null);
        }
        const [selectedOrganizationId, selectedPractitionerId] = organizationPractitionerIds.split('_');
        await props.onChange(selectedOrganizationId, selectedPractitionerId);
    }

    async function loadInitialOrganizationPractitioner() {
        if (props.initialPractitionerId) {
            const practitioner: Practitioner = await loadPractitioner(props.initialPractitionerId);

            if (props.initialOrganizationId && !loggedInUserHasGeneralPractitionersInOrganization) {
                const organization: IOrganization = await loadOrganization(props.initialOrganizationId);

                setInitialOrganizationPractitioner({ ...organization, practitioner });
            }
        }
    }
}
